import { useEffect, useState, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import api, { formatApiErrorDetail } from "../lib/api";
import { AppLayout } from "../components/AppLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card } from "../components/ui/card";
import { Skeleton } from "../components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { Building2, Plus, Loader2, Trash2, Pencil, Mail, Phone } from "lucide-react";

const EMPTY = { nome: "", referente: "", telefono: "", email: "", indirizzo: "", partita_iva: "", codice_fiscale: "", codice_univoco: "", note: "" };

const fiscali = (r) => [r.partita_iva && `P.IVA ${r.partita_iva}`, r.codice_fiscale && `CF ${r.codice_fiscale}`, r.codice_univoco && `SDI ${r.codice_univoco}`].filter(Boolean).join(" · ");


export default function RichiedentiPage() {
  const { user } = useAuth();
  const canEdit = user?.staff_role !== "operatore";
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = useCallback(async () => {
    const { data } = await api.get("/richiedenti");
    setItems(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (r) => {
    setEditing(r.id);
    setForm({ nome: r.nome || "", referente: r.referente || "", telefono: r.telefono || "",
      email: r.email || "", indirizzo: r.indirizzo || "", partita_iva: r.partita_iva || "", codice_fiscale: r.codice_fiscale || "", codice_univoco: r.codice_univoco || "", note: r.note || "" });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/richiedenti/${editing}`, form);
        toast.success("Richiedente aggiornato");
      } else {
        await api.post("/richiedenti", form);
        toast.success("Richiedente aggiunto");
      }
      setOpen(false);
      setForm(EMPTY);
      setEditing(null);
      await load();
    } catch (err) {
      toast.error("Errore", { description: formatApiErrorDetail(err.response?.data?.detail) });
    } finally { setSaving(false); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Eliminare il richiedente "${r.nome}"? L'operazione è irreversibile.`)) return;
    try {
      await api.delete(`/richiedenti/${r.id}`);
      await load();
      toast.success("Richiedente eliminato");
    } catch (err) {
      toast.error("Errore", { description: formatApiErrorDetail(err.response?.data?.detail) });
    }
  };

  return (
    <AppLayout>
      <div className="max-w-5xl mx-auto nx-fade-in">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold font-heading text-slate-900 tracking-tight flex items-center gap-2">
              <Building2 className="h-7 w-7 text-primary" /> Anagrafica Richiedenti
            </h1>
            <p className="text-sm text-slate-500 mt-1">Gestisci gli enti/soggetti che richiedono i trasporti</p>
          </div>
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(EMPTY); setEditing(null); } }}>
            <DialogTrigger asChild>
              <Button data-testid="add-richiedente-button" className="gap-2" onClick={openNew}>
                <Plus className="h-4 w-4" /> Nuovo Richiedente
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="font-heading">{editing ? "Modifica Richiedente" : "Nuovo Richiedente"}</DialogTitle>
                <DialogDescription>Questi nomi appaiono nel menu a tendina "Richiedente" della nuova richiesta.</DialogDescription>
              </DialogHeader>
              <form onSubmit={save} className="space-y-4 pt-2">
                <div>
                  <Label className="nx-label">Nome / Ragione Sociale</Label>
                  <Input data-testid="richiedente-nome" required className="mt-1.5"
                    value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} />
                </div>
                <div>
                  <Label className="nx-label">Referente</Label>
                  <Input data-testid="richiedente-referente" className="mt-1.5"
                    value={form.referente} onChange={(e) => setForm({ ...form, referente: e.target.value })} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="nx-label">Telefono</Label>
                    <Input data-testid="richiedente-telefono" className="mt-1.5"
                      value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} />
                  </div>
                  <div>
                    <Label className="nx-label">Email</Label>
                    <Input data-testid="richiedente-email" type="email" className="mt-1.5"
                      value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                  </div>
                </div>
                <div>
                  <Label className="nx-label">Indirizzo</Label>
                  <Input data-testid="richiedente-indirizzo" className="mt-1.5"
                    value={form.indirizzo} onChange={(e) => setForm({ ...form, indirizzo: e.target.value })} />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="nx-label">Partita IVA</Label>
                    <Input data-testid="richiedente-partita-iva" className="mt-1.5" maxLength={11}
                      value={form.partita_iva} onChange={(e) => setForm({ ...form, partita_iva: e.target.value.replace(/\D/g, "") })} />
                  </div>
                  <div>
                    <Label className="nx-label">Codice Fiscale</Label>
                    <Input data-testid="richiedente-codice-fiscale" className="mt-1.5 uppercase" maxLength={16}
                      value={form.codice_fiscale} onChange={(e) => setForm({ ...form, codice_fiscale: e.target.value.toUpperCase() })} />
                  </div>
                  <div>
                    <Label className="nx-label">Codice Univoco</Label>
                    <Input data-testid="richiedente-codice-univoco" className="mt-1.5 uppercase" maxLength={7}
                      value={form.codice_univoco} onChange={(e) => setForm({ ...form, codice_univoco: e.target.value.toUpperCase() })} />
                  </div>
                </div>
                <div>
                  <Label className="nx-label">Note</Label>
                  <Textarea data-testid="richiedente-note" rows={2} className="mt-1.5"
                    value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
                </div>
                <DialogFooter>
                  <Button type="submit" data-testid="richiedente-save" disabled={saving} className="gap-2 w-full">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (editing ? "Salva Modifiche" : "Aggiungi Richiedente")}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Card className="border-slate-200 overflow-hidden">
          {items === null ? (
            <div className="p-5 space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={`sk-${i}`} className="h-12 w-full" />)}</div>
          ) : items.length === 0 ? (
            <div className="py-16 text-center">
              <Building2 className="h-10 w-10 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500 text-sm">Nessun richiedente. Aggiungi il primo.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Richiedente</TableHead>
                  <TableHead>Contatti</TableHead>
                  <TableHead>Indirizzo</TableHead>
                  <TableHead className="text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((r) => (
                  <TableRow key={r.id} data-testid={`richiedente-row-${r.id}`}>
                    <TableCell>
                      <p className="font-medium text-slate-800 text-sm">{r.nome}</p>
                      {r.referente && <p className="text-xs text-slate-400">Ref: {r.referente}</p>}
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">
                      {r.email && <span className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5 text-slate-400" /> {r.email}</span>}
                      {r.telefono && <span className="flex items-center gap-1.5 mt-0.5"><Phone className="h-3.5 w-3.5 text-slate-400" /> {r.telefono}</span>}
                      {!r.email && !r.telefono && <span className="text-slate-300">—</span>}
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">
                      {r.indirizzo || <span className="text-slate-300">—</span>}
                      {(r.partita_iva || r.codice_fiscale || r.codice_univoco) && (
                        <p data-testid={`richiedente-fiscali-${r.id}`} className="text-xs text-slate-400 mt-0.5 font-mono">
                          {fiscali(r)}
                        </p>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        {canEdit && (
                          <Button data-testid={`richiedente-edit-${r.id}`} variant="ghost" size="icon"
                            className="h-8 w-8 text-slate-400 hover:text-primary" title="Modifica" onClick={() => openEdit(r)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                        )}
                        {canEdit && (
                          <Button data-testid={`richiedente-delete-${r.id}`} variant="ghost" size="icon"
                            className="h-8 w-8 text-slate-400 hover:text-red-600" title="Elimina" onClick={() => remove(r)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>
      </div>
    </AppLayout>
  );
}
